import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { Currency, Locale } from "@nh/shared";
import { useCart } from "../cart.context";
import { CartDialog } from "./CartDialog";

export interface CartPanelProps {
  currency: Currency;
  locale: Locale;
}

/**
 * El carrito es un PANEL LATERAL, no una página.
 *
 * Mandar a alguien a `/carrito` lo saca del catálogo: para seguir
 * comprando tiene que volver, y volver significa perder el filtro, el
 * orden y el sitio del desplazamiento donde estaba. El cajón deja la
 * tienda intacta debajo — se abre, se ajusta una cantidad, se cierra y se
 * sigue exactamente donde se estaba.
 *
 * ── Tres formas de cerrarlo ──────────────────────────────────────────
 * La X, el fondo y Escape. El fondo es un `<button>` de verdad y no un
 * `<div onClick>` para que también sea alcanzable; Escape lo maneja
 * `useFocusTrap` dentro del diálogo, porque quien navega con teclado no
 * tiene forma de "hacer clic fuera".
 *
 * ── No se monta cuando está cerrado ──────────────────────────────────
 * Así sus botones no participan en el orden de tabulación de la página
 * que hay debajo, y el efecto de `useFocusTrap` se limpia al desmontar —
 * que es justo lo que devuelve el foco al botón del carrito.
 */
export function CartPanel({ currency, locale }: CartPanelProps) {
  const { t } = useTranslation();
  const { isOpen, close } = useCart();

  /*
   * La página de debajo no se desplaza mientras el cajón está abierto. Sin
   * esto, la rueda del ratón sobre el fondo mueve el catálogo detrás del
   * panel y al cerrar nadie sabe dónde acabó — en móvil es peor: el gesto
   * de desplazar la lista del carrito arrastra la página entera.
   */
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[95] flex">
      <button
        type="button"
        onClick={close}
        aria-label={t("Cerrar el carrito")}
        className="absolute inset-0 h-full w-full cursor-default bg-ds-ink/30 backdrop-blur-sm"
      />

      <CartDialog currency={currency} locale={locale} onClose={close} />
    </div>
  );
}
